/**
 * U2 (R2) of plans/2026-08-25-002-lead-scanner-round2-plan.md —
 * client-side oversized-photo downscale — extended by the 2026-09-18 HEIC
 * auto-convert plan (plans/2026-09-18-001-lead-scanner-heic-bulk-submit-plan.md
 * §1) to run on EVERY successfully-decoded photo, not only oversized ones.
 *
 * This module now does two jobs:
 *
 * 1. Downscale a photo wider/taller than 8000px per side, which is Claude's
 *    hard per-side limit for image inputs. Above that limit, extraction
 *    fails with a generic "photo extraction unavailable" message that gives
 *    the rep no way to recover in the moment.
 * 2. Normalize ANY browser-decodable container to JPEG — the motivating case
 *    is HEIC on Safari/WebKit. Only Safari/WebKit (iOS and macOS) can decode
 *    HEIC via `createImageBitmap` — Apple exposes ImageIO's HEIC decoder to
 *    WebKit. Chromium (Chrome, Edge, Brave, Arc) cannot decode HEIC on any
 *    platform, by long-standing deliberate decision over HEVC licensing. A
 *    HEIC upload on Chromium therefore still throws at `createImageBitmap`
 *    below and falls through to returning the original file untouched, which
 *    the server then rejects with `UNSUPPORTED_PHOTO_FORMAT_MESSAGE`
 *    (`extraction.ts`) exactly as it does today — this module makes no
 *    attempt to detect or work around that; it only converts what the
 *    browser itself was already able to decode.
 *
 * Both jobs share one redraw: the decode already happened (step 2 needs it
 * regardless), so the only incremental cost of also normalizing an
 * under-cap photo is the same `drawImage` + `convertToBlob` the oversized
 * path already paid for.
 *
 * Two halves, deliberately split for testability:
 *
 * 1. `resizeTarget`/`encodeTarget` — pure decision math. No DOM, no I/O.
 *    `resizeTarget` decides whether a resize is needed and, if so, the
 *    target dimensions (longest side capped at `maxSide`, aspect ratio
 *    preserved, integer sides >= 1); `maxSide` defaults to 7500 — a
 *    deliberate safety margin under Claude's 8000px hard limit, not the
 *    limit itself. `encodeTarget` wraps it into a total function (always
 *    returns dimensions to redraw at, never `null`) since every decoded
 *    photo is now redrawn regardless of size.
 * 2. `preparePhotoForExtraction` — the actual redraw, via
 *    `createImageBitmap` → an offscreen canvas → `canvas.toBlob`. This half
 *    needs a real browser image decoder and is browser-QA'd, not
 *    unit-tested (see `__tests__/photo-resize.test.ts`'s header comment).
 *
 * Never throws: this mirrors `qr-decode.ts`'s `decodeQrFromPhoto` stance —
 * any decode/encode failure here (corrupt file, unsupported/undecodable
 * container such as HEIC on Chromium, missing `OffscreenCanvas`, `toBlob`
 * returning null) falls back to returning the ORIGINAL file untouched. The
 * server's own sniffing/rejection stays the safety net for whatever this
 * browser couldn't decode; a client-side conversion failure must never
 * become a harder failure than "we didn't convert it."
 *
 * Side benefit: any other browser-decodable-but-server-unsupported container
 * (e.g. AVIF, BMP, TIFF on Safari) is fixed by the same change with zero
 * format-specific code — this module never inspects file type or extension.
 *
 * Callers: `components/PhotoIntake.tsx` wraps ONLY the `extractBadgeFromPhoto`
 * dependency with this — the QR decode leg (`qr-decode.ts`) reads the
 * ORIGINAL file, because downscaling/re-encoding destroys the QR module
 * pattern (documented there and in the prototype's DEV NOTE), and zxing-wasm
 * decodes raw bytes in wasm regardless (`qr-decode.ts:201`), so it couldn't
 * benefit from the conversion even if it received it.
 */

/**
 * Decides whether `width`x`height` needs to be resized to fit within
 * `maxSide` on its longest side, and if so, what the new integer dimensions
 * are (aspect ratio preserved).
 *
 * Returns `null` when both sides are already within `maxSide` — no resize
 * needed. Otherwise returns `{width, height}`, each a positive integer
 * (>= 1; the short side of an extreme aspect ratio is floored at 1 rather
 * than allowed to round to 0).
 */
export function resizeTarget(
  width: number,
  height: number,
  maxSide = 7500,
): { width: number; height: number } | null {
  if (width <= maxSide && height <= maxSide) return null;

  const scale = maxSide / Math.max(width, height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * `resizeTarget`, made total: always returns the dimensions to redraw
 * `width`x`height` at, never `null`. Under `maxSide` it returns the
 * dimensions unchanged (still redrawn, just not downscaled) — over it, it
 * returns exactly `resizeTarget`'s result. `resizeTarget` itself and its
 * `null`-means-no-resize contract are untouched; this is a separate, total
 * wrapper so its own tests (and its `null` case) keep passing as-is.
 */
export function encodeTarget(width: number, height: number, maxSide = 7500): { width: number; height: number } {
  return resizeTarget(width, height, maxSide) ?? { width, height };
}

/**
 * Redraws `file` to JPEG (quality ~0.9 — small enough to matter for an
 * event-floor upload, high enough not to visibly degrade OCR-relevant
 * detail) on EVERY successful decode, downscaling first if it exceeds
 * `encodeTarget`'s cap. Returns the original `file` untouched when anything
 * in the decode/draw/encode pipeline fails — including a browser (Chromium)
 * that cannot decode the container (e.g. HEIC) at all.
 */
export async function preparePhotoForExtraction(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    try {
      const target = encodeTarget(bitmap.width, bitmap.height);

      const canvas = new OffscreenCanvas(target.width, target.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.drawImage(bitmap, 0, 0, target.width, target.height);

      const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.9 });
      if (!blob) return file;

      const name = file.name.replace(/\.[^./]+$/, '') + '.jpg';
      return new File([blob], name, { type: 'image/jpeg' });
    } finally {
      bitmap.close();
    }
  } catch {
    return file;
  }
}
